// src/pages/Relatorios.js
import React, { useEffect, useState, useMemo, useCallback, useRef } from 'react';
import axios from 'axios';
import toast from 'react-hot-toast';
import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import {
    FunnelIcon,
    ArrowDownTrayIcon,
    ExclamationTriangleIcon,
    BanknotesIcon,
    ChartBarIcon,
    DocumentChartBarIcon,
    ArrowPathIcon,
    ChevronDownIcon,
    ChevronUpIcon,
    InformationCircleIcon
} from '@heroicons/react/24/outline';
import { format } from 'date-fns';
import { Badge } from '../components/ui/badge';

/** Mongo/API às vezes devolve números como string; .toFixed em string quebra o React (tela em branco). */
function num(v) {
    const x = Number(v);
    return Number.isFinite(x) ? x : 0;
}
function fmt4(v) {
    return num(v).toFixed(4);
}
function fmtMin(v) {
    return num(v).toFixed(2);
}

function normalizeUraSummary(summary = {}) {
    return {
        pending: num(summary.pending),
        in_progress: num(summary.in_progress),
        done: num(summary.done),
        failed: num(summary.failed),
    };
}

function buildUraSummaryFromGroups(groups = []) {
    return groups.reduce(
        (acc, group) => {
            acc.pending += num(group?.summary?.pending);
            acc.in_progress += num(group?.summary?.in_progress);
            acc.done += num(group?.summary?.done);
            acc.failed += num(group?.summary?.failed);
            return acc;
        },
        { pending: 0, in_progress: 0, done: 0, failed: 0 }
    );
}

/**
 * GET /api/campanhas pode vir como array cru ou envelopado (proxy/tenant/API gateway).
 * Garante array para não quebrar .filter / .map.
 */
/** Resposta foi index.html do SPA (API não alcançada — ex.: REACT_APP_API_BASE_URL vazio no build). */
function isHtmlOrNonJsonBody(data) {
    if (typeof data === 'string') {
        const t = data.trim().slice(0, 64).toLowerCase();
        return t.startsWith('<!doctype') || t.startsWith('<html');
    }
    return false;
}

function normalizeListaCampanhas(payload) {
    if (Array.isArray(payload)) return payload;
    if (payload == null) return [];
    if (typeof payload !== 'object') return [];
    const nestedKeys = ['campanhas', 'data', 'items', 'results', 'docs', 'rows', 'records', 'result'];
    for (const k of nestedKeys) {
        if (Array.isArray(payload[k])) return payload[k];
    }
    // Um nível a mais: { data: { campanhas: [...] } } ou { result: { data: [...] } }
    for (const k of nestedKeys) {
        const inner = payload[k];
        if (inner && typeof inner === 'object') {
            for (const k2 of nestedKeys) {
                if (Array.isArray(inner[k2])) return inner[k2];
            }
        }
    }
    return [];
}

const ReportSectionHeader = ({ title, icon }) => (
    <div className="flex items-center gap-3 mb-4 mt-8">
        {icon}
        <h2 className="text-2xl font-semibold">{title}</h2>
    </div>
);

// Componente para o badge de status
const StatusBadge = ({ status }) => {
    const statusStyles = {
        em_fila: 'bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-950 dark:text-amber-200 dark:border-amber-900',
        reenviada: 'bg-orange-50 text-orange-700 border-orange-200 dark:bg-orange-950 dark:text-orange-200 dark:border-orange-900',
        atendido: 'bg-green-50 text-green-700 border-green-200 dark:bg-green-950 dark:text-green-200 dark:border-green-900',
        ativa: 'bg-blue-50 text-blue-700 border-blue-200 dark:bg-blue-950 dark:text-blue-200 dark:border-blue-900',
        falhou: 'bg-rose-50 text-rose-700 border-rose-200 dark:bg-rose-950 dark:text-rose-200 dark:border-rose-900',
        cancelada: 'bg-muted text-muted-foreground border-border',
        // Status antigos (compatibilidade com dados existentes)
        pendente: 'bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-950 dark:text-amber-200 dark:border-amber-900',
        concluída: 'bg-green-50 text-green-700 border-green-200 dark:bg-green-950 dark:text-green-200 dark:border-green-900',
        concluida: 'bg-green-50 text-green-700 border-green-200 dark:bg-green-950 dark:text-green-200 dark:border-green-900',
        sucesso: 'bg-green-50 text-green-700 border-green-200 dark:bg-green-950 dark:text-green-200 dark:border-green-900',
        erro: 'bg-rose-50 text-rose-700 border-rose-200 dark:bg-rose-950 dark:text-rose-200 dark:border-rose-900',
        em_progresso: 'bg-blue-50 text-blue-700 border-blue-200 dark:bg-blue-950 dark:text-blue-200 dark:border-blue-900',
        em_andamento: 'bg-purple-500 text-purple-100 border-purple-600',
        enviado: 'bg-blue-50 text-blue-700 border-blue-200 dark:bg-blue-950 dark:text-blue-200 dark:border-blue-900',
    };
    return <Badge className={statusStyles[status] || 'bg-muted text-muted-foreground border-border'}>{status}</Badge>;
};

export default function Relatorios() {
    const [campanhas, setCampanhas] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const userData = useMemo(() => {
        try {
            return JSON.parse(localStorage.getItem('userData') || '{}');
        } catch (e) {
            return {};
        }
    }, []);
    const clienteId = userData?.clienteId;
    const isAdmin = userData.role === 'admin' || userData.role === 'super_admin';
    const isSuperAdmin = userData.role === 'super_admin';
    // Mesmo fallback do Login.js: sem env no build do Docker, URL relativa /api cai no Nginx (HTML) e quebra tudo.
    const apiBaseUrl = (process.env.REACT_APP_API_BASE_URL || 'https://ia.discador.net').replace(/\/+$/, '');
    // const apiBaseUrl = process.env.REACT_APP_API_BASE_URL || 'http://localhost:3000/';
    // Estado para armazenar a taxa do cliente
    const [clienteTaxa, setClienteTaxa] = useState(0);
    const [filtros, setFiltros] = useState({
        tipo: '',
        status: '',
        dataInicial: '',
        dataFinal: ''
    });
    // --- ESTADO PARA RELATÓRIO DE CUSTOS ---
    const [costReport, setCostReport] = useState([]);
    const [costLoading, setCostLoading] = useState(true);
    const [costError, setCostError] = useState('');
    const [costClienteFiltro, setCostClienteFiltro] = useState('');
    const [costCurrentPage, setCostCurrentPage] = useState(1);
    const COST_CLIENTS_PER_PAGE = 10;
    const [currentPage, setCurrentPage] = useState(1);
    const itemsPerPage = 10;
    const [custoFiltros, setCustoFiltros] = useState({
        dataInicial: '',
        dataFinal: ''
    });
    const [expandedClients, setExpandedClients] = useState({});
    const [isNewFormat, setIsNewFormat] = useState(false);
    const [uraQueueLoading, setUraQueueLoading] = useState(false);
    const [uraReleaseLoading, setUraReleaseLoading] = useState(false);
    const [uraQueueError, setUraQueueError] = useState('');
    const [uraQueueSummary, setUraQueueSummary] = useState({ pending: 0, in_progress: 0, done: 0, failed: 0 });
    const [uraQueueGroups, setUraQueueGroups] = useState([]);
    const [uraClientOptions, setUraClientOptions] = useState([]);
    const [expandedQueueClients, setExpandedQueueClients] = useState({});
    const [expandedQueuePhoneNumbers, setExpandedQueuePhoneNumbers] = useState({});
    const [queueGroupItemsMap, setQueueGroupItemsMap] = useState({});
    const [queueGroupLoadingMap, setQueueGroupLoadingMap] = useState({});
    const [uraQueueFilters, setUraQueueFilters] = useState({
        status: '',
        clienteId: '',
        phoneNumberId: ''
    });
    const [creditHistoryLoading, setCreditHistoryLoading] = useState(false);
    const [creditHistoryError, setCreditHistoryError] = useState('');
    const [creditHistoryItems, setCreditHistoryItems] = useState([]);
    const [creditHistoryFilter, setCreditHistoryFilter] = useState('');
    const [creditHistoryPage, setCreditHistoryPage] = useState(1);
    const [creditHistoryTotalPages, setCreditHistoryTotalPages] = useState(1);
    const CREDIT_HISTORY_PER_PAGE = 10;
    const uraQueuePageSize = 10;
    /** Evita rajadas de GET /queue (Mongo) em cada evento SSE */
    const lastUraQueueFetchRef = useRef(0);
    const uraQueueAuthFailedRef = useRef(false);
    const sessionExpiredToastShownRef = useRef(false);
    const fetchUraQueueRef = useRef(async () => {});
    const uraSseRef = useRef(null);

    // Função para buscar a taxa do cliente
    const fetchClienteTaxa = useCallback(async () => {
        if (!clienteId) {
            return;
        }
        try {
            const token = localStorage.getItem('token');
            const headers = token ? { Authorization: `Bearer ${token}` } : {};
            const response = await axios.get(`${apiBaseUrl}/api/usuarios/${clienteId}/taxa`, { headers });
            setClienteTaxa(num(response.data.taxa));
        } catch (err) {
            console.error('Erro ao buscar taxa do cliente:', err);
            if (err.response) {
                console.error('Resposta do servidor:', err.response.data);
                console.error('Status code:', err.response.status);
            } else if (err.request) {
                console.error('Requisição feita mas não houve resposta:', err.request);
            } else {
                console.error('Erro:', err.message);
            }
            setClienteTaxa(0); // Valor padrão em caso de erro
        }
    }, [apiBaseUrl, clienteId]);

    // --- FUNÇÕES DE BUSCA DE DADOS (DATA FETCHING) ---
    const getAuthConfig = () => {
        const token = localStorage.getItem('token');
        if (!token) {
            toast.error('Sessão expirada. Faça login novamente.');
            return null;
        }
        return { headers: { Authorization: `Bearer ${token}` } };
    };

    const fetchCampaigns = useCallback(async () => {
        setLoading(true);
        const authConfig = getAuthConfig();
        if (!authConfig) {
            setLoading(false);
            return;
        }
        const params = !isAdmin && clienteId ? { clienteId } : {};
        try {
            const res = await axios.get(`${apiBaseUrl}/api/campanhas`, { ...authConfig, params });
            setCampanhas(normalizeListaCampanhas(res.data));
            setError('');
        } catch (err) {
            setError('Erro ao carregar os dados das campanhas.');
            toast.error('Não foi possível buscar os dados.');
            console.error(err);
        } finally {
            setLoading(false);
        }
    }, [apiBaseUrl, isAdmin, clienteId]);

    const fetchCostReport = useCallback(async () => {
        setCostLoading(true);
        const toastId = toast.loading('Carregando relatório de custos...');
        try {
            const authConfig = getAuthConfig();
            if (!authConfig) {
                setCostLoading(false);
                return;
            }
            const params = {
                id: userData.id,
                clienteId: userData.clienteId,
                role: userData.role,
                dataInicial: custoFiltros.dataInicial,
                dataFinal: custoFiltros.dataFinal
            };
            const res = await axios.get(`${apiBaseUrl}/api/relatorios/custo-por-cliente`, {
                params,
                ...authConfig,
            });
            const ct = res.headers?.['content-type'] || '';
            if (ct.includes('text/html') || isHtmlOrNonJsonBody(res.data)) {
                setIsNewFormat(false);
                setCostReport([]);
                setCostError('Não foi possível carregar o relatório: a API devolveu HTML (painel sem REACT_APP_API_BASE_URL ou proxy /api).');
                toast.error('Configure REACT_APP_API_BASE_URL no build ou proxy /api no Nginx.', { id: toastId });
                return;
            }
            if (res.data && typeof res.data === 'object' && (res.data.resumo || res.data.detalhesPorCliente)) {
                setIsNewFormat(true);
                setCostReport(Array.isArray(res.data.detalhesPorCliente) ? res.data.detalhesPorCliente : []);
            } else {
                setIsNewFormat(false);
                setCostReport(Array.isArray(res.data) ? res.data : []);
            }
            setCostError('');
            toast.success('Relatório de custos carregado com sucesso!', { id: toastId });
        } catch (err) {
            const status = err.response?.status;
            if (status === 401) {
                setCostError('Sessão expirada. Faça login novamente.');
                toast.error('Sessão expirada. Faça login novamente.', { id: toastId });
            } else {
                setCostError('Erro ao carregar o relatório de custos.');
                toast.error('Não foi possível buscar os dados de custo.', { id: toastId });
            }
            console.error('Erro na requisição:', err);
        } finally {
            setCostLoading(false);
        }
    }, [apiBaseUrl, custoFiltros.dataInicial, custoFiltros.dataFinal, userData.id, userData.clienteId, userData.role]);

    const fetchCreditHistory = useCallback(async (page = 1, filtro = creditHistoryFilter) => {
        if (!isSuperAdmin) return;
        setCreditHistoryLoading(true);
        setCreditHistoryError('');
        try {
            const authConfig = getAuthConfig();
            if (!authConfig) return;
            const params = {
                page,
                limit: CREDIT_HISTORY_PER_PAGE,
                filtro: filtro || undefined,
            };
            const res = await axios.get(`${apiBaseUrl}/api/usuarios/creditos/historico-geral`, {
                ...authConfig,
                params,
            });
            setCreditHistoryItems(Array.isArray(res.data?.items) ? res.data.items : []);
            setCreditHistoryPage(Number(res.data?.page || 1));
            setCreditHistoryTotalPages(Math.max(1, Number(res.data?.totalPages || 1)));
        } catch (err) {
            setCreditHistoryError('Erro ao carregar histórico de créditos.');
            console.error('Erro ao carregar histórico geral de créditos:', err);
        } finally {
            setCreditHistoryLoading(false);
        }
    }, [apiBaseUrl, isSuperAdmin, creditHistoryFilter]);

    const fetchUraClientOptions = useCallback(async () => {
        if (!isAdmin && !isSuperAdmin) return;
        try {
            const authConfig = getAuthConfig();
            if (!authConfig) return;
            const res = await axios.get(`${apiBaseUrl}/api/ura/client-options`, authConfig);
            setUraClientOptions(
                (res.data?.clientOptions || []).map((c) => ({
                    id: c.id,
                    label: c.nome || c.email || c.id
                }))
            );
        } catch (err) {
            console.error('Erro ao carregar lista de clientes (filtro URA):', err);
        }
    }, [apiBaseUrl, isAdmin, isSuperAdmin]);

    const URA_QUEUE_AUTO_MIN_MS = 5000;

    const fetchUraQueue = useCallback(async (options = {}) => {
        const force = options.force === true;
        if (!force) {
            const now = Date.now();
            if (now - lastUraQueueFetchRef.current < URA_QUEUE_AUTO_MIN_MS) {
                return;
            }
            lastUraQueueFetchRef.current = now;
        } else {
            lastUraQueueFetchRef.current = Date.now();
        }

        if (uraQueueAuthFailedRef.current && !force) {
            return;
        }

        setUraQueueLoading(true);
        setUraQueueError('');
        try {
            const authConfig = getAuthConfig();
            if (!authConfig) {
                setUraQueueLoading(false);
                return;
            }

            const params = {
                groupByClient: true,
                limit: 200
            };

            if (uraQueueFilters.status) params.status = uraQueueFilters.status;
            if (uraQueueFilters.phoneNumberId) params.phoneNumberId = uraQueueFilters.phoneNumberId;
            if ((isAdmin || isSuperAdmin) && uraQueueFilters.clienteId) {
                params.clienteId = uraQueueFilters.clienteId;
            }

            const res = await axios.get(`${apiBaseUrl}/api/ura/queue`, {
                ...authConfig,
                params
            });

            uraQueueAuthFailedRef.current = false;
            sessionExpiredToastShownRef.current = false;
            const groups = res.data?.groupedByClient || [];
            const apiSummary = normalizeUraSummary(res.data?.summary);
            const groupedSummary = buildUraSummaryFromGroups(groups);
            const hasGroupedData = groups.length > 0;
            const finalSummary = hasGroupedData ? groupedSummary : apiSummary;

            setUraQueueGroups(groups);
            setUraQueueSummary(finalSummary);
        } catch (err) {
            const status = err.response?.status;
            if (status === 401) {
                uraQueueAuthFailedRef.current = true;
                if (uraSseRef.current) {
                    try {
                        uraSseRef.current.close();
                    } catch (e) {
                        /* ignore */
                    }
                    uraSseRef.current = null;
                }
                setUraQueueError('Sessão expirada. Faça login novamente.');
                if (!sessionExpiredToastShownRef.current) {
                    sessionExpiredToastShownRef.current = true;
                    toast.error('Sessão expirada. Faça login novamente.');
                }
            } else {
                setUraQueueError('Erro ao carregar fila URA.');
            }
            console.error(err);
        } finally {
            setUraQueueLoading(false);
        }
    }, [apiBaseUrl, uraQueueFilters, isAdmin, isSuperAdmin]);

    fetchUraQueueRef.current = fetchUraQueue;

    const refreshUraQueueManual = useCallback(() => {
        fetchUraQueue({ force: true });
    }, [fetchUraQueue]);

    const releaseUraStuckQueue = useCallback(async () => {
        if (!isAdmin && !isSuperAdmin) return;
        const scope = uraQueueFilters.phoneNumberId
            ? 'somente para o PhoneNumberId filtrado'
            : 'em todas as linhas do seu escopo';
        if (!window.confirm(
            `Marcar como falha os itens em "em progresso" (${scope}) e liberar a fila para as próximas ligações?`
        )) {
            return;
        }
        setUraReleaseLoading(true);
        const loadingId = toast.loading('Liberando fila…');
        try {
            const authConfig = getAuthConfig();
            if (!authConfig) {
                toast.dismiss(loadingId);
                return;
            }
            const res = await axios.post(
                `${apiBaseUrl}/api/ura/queue/release-stuck`,
                {
                    mode: 'fail',
                    ...(uraQueueFilters.phoneNumberId ? { phoneNumberId: uraQueueFilters.phoneNumberId } : {})
                },
                {
                    ...authConfig,
                    headers: {
                        ...authConfig.headers,
                        'Content-Type': 'application/json'
                    }
                }
            );
            toast.dismiss(loadingId);
            const n = res.data?.released ?? 0;
            toast.success(n > 0 ? `${n} item(ns) liberados.` : (res.data?.message || 'Nada para liberar.'));
            fetchUraQueue({ force: true });
        } catch (err) {
            toast.dismiss(loadingId);
            const msg = err.response?.data?.message
                || err.response?.data?.error
                || (err.response?.status === 403 ? 'Sem permissão (apenas admin).' : null)
                || err.message
                || 'Erro ao liberar fila.';
            toast.error(msg);
            console.error(err);
        } finally {
            setUraReleaseLoading(false);
        }
    }, [apiBaseUrl, isAdmin, isSuperAdmin, uraQueueFilters.phoneNumberId, fetchUraQueue]);

    useEffect(() => {
        fetchUraClientOptions();
    }, [fetchUraClientOptions]);

    useEffect(() => {
        if (clienteId) {
            fetchClienteTaxa();
        }
    }, [clienteId, fetchClienteTaxa]);

    // Busca campanhas e relatório de custos ao montar o componente
    // Para buscar campanhas - apenas na montagem
    useEffect(() => {
        fetchCampaigns();
    }, [fetchCampaigns]); // Array vazio para executar apenas na montagem

    // Para buscar relatório de custos - quando filtros mudarem
    useEffect(() => {
        fetchCostReport();
    }, [fetchCostReport]); // Apenas quando os filtros mudarem

    useEffect(() => {
        if (!isSuperAdmin) return;
        fetchCreditHistory(1, creditHistoryFilter);
    }, [isSuperAdmin, fetchCreditHistory, creditHistoryFilter]);

    /** Recarrega fila quando filtros mudam (inclui montagem inicial) */
    useEffect(() => {
        fetchUraQueue({ force: true });
    }, [fetchUraQueue]);

    /** SSE + polling: não depende de fetchUraQueue para não reconectar a cada mudança de filtro */
    useEffect(() => {
        let reconnectTimer = null;
        let debounceTimer = null;
        let eventSource = null;
        let reconnectBackoffMs = 5000;

        const scheduleFetch = () => {
            if (debounceTimer) clearTimeout(debounceTimer);
            debounceTimer = setTimeout(() => {
                if (!document.hidden && !uraQueueAuthFailedRef.current) {
                    fetchUraQueueRef.current({ force: false });
                }
            }, 1500);
        };

        const connectSSE = () => {
            if (uraQueueAuthFailedRef.current) return;
            const token = localStorage.getItem('token');
            if (!token) return;
            const streamUrl = `${apiBaseUrl}/api/ura/queue/stream?token=${encodeURIComponent(token)}`;
            try {
                eventSource = new EventSource(streamUrl);
            } catch (e) {
                console.error('[Relatorios] EventSource inválido:', e);
                return;
            }
            uraSseRef.current = eventSource;

            eventSource.onopen = () => {
                reconnectBackoffMs = 5000;
            };

            eventSource.onmessage = () => {
                scheduleFetch();
            };

            eventSource.onerror = () => {
                if (eventSource) {
                    eventSource.close();
                }
                uraSseRef.current = null;
                if (uraQueueAuthFailedRef.current) return;
                if (!reconnectTimer) {
                    reconnectTimer = setTimeout(() => {
                        reconnectTimer = null;
                        reconnectBackoffMs = Math.min(reconnectBackoffMs * 2, 60000);
                        connectSSE();
                    }, reconnectBackoffMs);
                }
            };
        };

        connectSSE();

        const interval = setInterval(() => {
            if (!document.hidden && !uraQueueAuthFailedRef.current) {
                fetchUraQueueRef.current({ force: false });
            }
        }, 180000);

        return () => {
            clearInterval(interval);
            if (debounceTimer) clearTimeout(debounceTimer);
            if (reconnectTimer) clearTimeout(reconnectTimer);
            if (eventSource) eventSource.close();
            uraSseRef.current = null;
        };
    }, [apiBaseUrl]);

    const handleFilterChange = (e) => {
        const { name, value } = e.target;
        setFiltros(prev => ({ ...prev, [name]: value }));
    };

    const campanhasFiltradas = useMemo(() => {
        const lista = normalizeListaCampanhas(campanhas);
        return lista.filter(c => {
            const dataCampanha = new Date(c.criadoEm);
            const dataInicialFiltro = filtros.dataInicial ? new Date(filtros.dataInicial) : null;
            const dataFinalFiltro = filtros.dataFinal ? new Date(filtros.dataFinal) : null;
            // Ajuste para incluir o dia inteiro na data final
            if (dataFinalFiltro) dataFinalFiltro.setHours(23, 59, 59, 999);
            const dentroDoPeriodo = (!dataInicialFiltro || dataCampanha >= dataInicialFiltro) && (!dataFinalFiltro || dataCampanha <= dataFinalFiltro);
            const tipoMatch = !filtros.tipo || c.tipo === filtros.tipo;
            const statusMatch = !filtros.status || c.status === filtros.status;
            return dentroDoPeriodo && tipoMatch && statusMatch;
        });
    }, [campanhas, filtros]);

    useEffect(() => {
        setCurrentPage(1);
    }, [filtros]);

    const totalPages = Math.ceil(campanhasFiltradas.length / itemsPerPage) || 1;
    const campanhasPaginadas = useMemo(() => {
        const start = (currentPage - 1) * itemsPerPage;
        return campanhasFiltradas.slice(start, start + itemsPerPage);
    }, [campanhasFiltradas, currentPage]);

    // Função para calcular custos com taxa, agora considerando o costBreakdown para admins
    const calcularCustosComTaxa = useCallback(() => {
        if (!Array.isArray(costReport) || costReport.length === 0) return [];

        // Adiciona o cliente atual se for admin (como antes)
        let dados = [...costReport];
        if (isAdmin && userData.id) {
            const adminJaIncluido = dados.some(c => c.clienteId === userData.id);
            if (!adminJaIncluido) {
                dados.push({
                    clienteId: userData.id,
                    nomeCliente: 'Admin (Você)',
                    emailCliente: userData.email,
                    taxaCliente: 0,
                    totalChamadas: 0,
                    totalMinutos: 0,
                    custoBaseTotal: 0,
                    taxasTotais: 0,
                    custoFinalTotal: 0,
                    custoComTaxa: 0,
                    assistentes: []
                });
            }
        }

        // Processa os dados
        return dados.map(cliente => {
            // Se já tiver custoComTaxa calculado (formato novo), mantemos
            if (isNewFormat && cliente.custoComTaxa !== undefined) {
                // Para admins, adicionamos o costBreakdown ao objeto
                if (isAdmin && cliente.costBreakdown) {
                    return {
                        ...cliente,
                        // Adicionamos os campos do costBreakdown no nível superior para fácil acesso
                        ...cliente.costBreakdown,
                        // Mantemos o costBreakdown original também
                        costBreakdown: cliente.costBreakdown
                    };
                }
                return cliente;
            }

            // Calcula o custo com a taxa do cliente
            const taxaCliente = cliente.taxaCliente || clienteTaxa || 0;
            const totalChamadas = cliente.totalChamadas || 0;
            const taxaTotal = taxaCliente * totalChamadas;

            // Se for o formato novo, usamos custoFinalTotal como base
            if (isNewFormat) {
                const resultado = {
                    ...cliente,
                    taxaTotal: cliente.taxasTotais || taxaTotal,
                    custoComTaxa: (cliente.custoFinalTotal || 0) + (cliente.taxaCliente * totalChamadas)
                };

                // Para admins, adicionamos o costBreakdown se existir
                if (isAdmin && cliente.costBreakdown) {
                    return {
                        ...resultado,
                        ...cliente.costBreakdown,
                        costBreakdown: cliente.costBreakdown
                    };
                }

                return resultado;
            } else {
                // Formato antigo - calculamos com base nos campos disponíveis
                const custoBase = cliente.custoBaseTotal || 0;
                const custoFinal = cliente.custoFinalTotal || 0;
                // Usamos o custoFinal se disponível, senão usamos o custoBase + taxas
                const custoParaCalculo = custoFinal > 0 ? custoFinal : (custoBase + (cliente.taxasTotais || 0));

                const resultado = {
                    ...cliente,
                    taxaCliente: taxaCliente,
                    taxaTotal: taxaTotal,
                    custoComTaxa: custoParaCalculo + taxaTotal
                };

                // Para admins, adicionamos o costBreakdown se existir (formato antigo não tinha)
                if (isAdmin && cliente.costBreakdown) {
                    return {
                        ...resultado,
                        ...cliente.costBreakdown,
                        costBreakdown: cliente.costBreakdown
                    };
                }

                return resultado;
            }
        });
    }, [costReport, clienteTaxa, isNewFormat, isAdmin, userData.id, userData.email]);

    const custosComTaxa = useMemo(() => calcularCustosComTaxa(), [calcularCustosComTaxa]);
    const custosComTaxaFiltrados = useMemo(() => {
        const termo = costClienteFiltro.trim().toLowerCase();
        if (!termo) return custosComTaxa;
        return custosComTaxa.filter((cliente) => {
            const nome = String(cliente.nomeCliente || '').toLowerCase();
            const email = String(cliente.emailCliente || '').toLowerCase();
            const id = String(cliente.clienteId || '').toLowerCase();
            return nome.includes(termo) || email.includes(termo) || id.includes(termo);
        });
    }, [custosComTaxa, costClienteFiltro]);
    const costTotalPages = Math.max(1, Math.ceil(custosComTaxaFiltrados.length / COST_CLIENTS_PER_PAGE));
    const custosComTaxaPaginados = useMemo(() => {
        const start = (costCurrentPage - 1) * COST_CLIENTS_PER_PAGE;
        return custosComTaxaFiltrados.slice(start, start + COST_CLIENTS_PER_PAGE);
    }, [custosComTaxaFiltrados, costCurrentPage]);
    const costSummary = useMemo(() => custosComTaxaFiltrados.reduce((acc, cliente) => {
        acc.totalChamadas += num(cliente.totalChamadas);
        acc.totalMinutos += num(cliente.totalMinutos);
        acc.custoBaseTotal += num(cliente.custoBaseTotal);
        acc.custoFinalComTaxa += num(cliente.custoFinalTotal) + num(cliente.taxaCliente) * num(cliente.totalChamadas);
        acc.stt += num(cliente.stt ?? cliente.costBreakdown?.stt);
        acc.llm += num(cliente.llm ?? cliente.costBreakdown?.llm);
        acc.tts += num(cliente.tts ?? cliente.costBreakdown?.tts);
        acc.vapi += num(cliente.vapi ?? cliente.costBreakdown?.vapi);
        acc.chat += num(cliente.chat ?? cliente.costBreakdown?.chat);
        return acc;
    }, {
        totalChamadas: 0,
        totalMinutos: 0,
        custoBaseTotal: 0,
        custoFinalComTaxa: 0,
        stt: 0,
        llm: 0,
        tts: 0,
        vapi: 0,
        chat: 0,
    }), [custosComTaxaFiltrados]);
    useEffect(() => {
        setCostCurrentPage(1);
    }, [costClienteFiltro, custoFiltros.dataInicial, custoFiltros.dataFinal]);
    useEffect(() => {
        if (costCurrentPage > costTotalPages) {
            setCostCurrentPage(costTotalPages);
        }
    }, [costCurrentPage, costTotalPages]);

    // Função para alternar a expansão de um cliente
    const toggleClientExpanded = (clienteId) => {
        setExpandedClients(prev => ({
            ...prev,
            [clienteId]: !prev[clienteId]
        }));
    };
    const toggleQueueClientExpanded = (clienteId) => {
        setExpandedQueueClients(prev => ({
            ...prev,
            [clienteId]: !prev[clienteId]
        }));
    };
    const toggleQueuePhoneExpanded = (clienteId, phoneNumberId) => {
        const key = `${clienteId || 'sem-cliente'}:${phoneNumberId || 'sem-phone'}`;
        const willOpen = !expandedQueuePhoneNumbers[key];
        setExpandedQueuePhoneNumbers(prev => ({
            ...prev,
            [key]: willOpen
        }));
        if (willOpen) {
            fetchQueueGroupItems(clienteId, phoneNumberId, 1);
        }
    };

    const fetchQueueGroupItems = useCallback(async (targetClienteId, targetPhoneNumberId, page = 1) => {
        const key = `${targetClienteId || 'sem-cliente'}:${targetPhoneNumberId || 'sem-phone'}`;
        setQueueGroupLoadingMap(prev => ({ ...prev, [key]: true }));
        try {
            const authConfig = getAuthConfig();
            if (!authConfig) return;

            const params = {
                page,
                pageSize: uraQueuePageSize,
                phoneNumberId: targetPhoneNumberId
            };

            if ((isAdmin || isSuperAdmin) && targetClienteId) {
                params.clienteId = targetClienteId;
            }
            if (uraQueueFilters.status) {
                params.status = uraQueueFilters.status;
            }

            const res = await axios.get(`${apiBaseUrl}/api/ura/queue/items`, {
                ...authConfig,
                params
            });

            setQueueGroupItemsMap(prev => ({
                ...prev,
                [key]: {
                    page: res.data?.page || page,
                    totalPages: res.data?.totalPages || 1,
                    total: res.data?.total || 0,
                    items: res.data?.items || []
                }
            }));
        } catch (err) {
            console.error('Erro ao buscar itens paginados da fila URA:', err);
        } finally {
            setQueueGroupLoadingMap(prev => ({ ...prev, [key]: false }));
        }
    }, [apiBaseUrl, isAdmin, isSuperAdmin, uraQueueFilters.status]);

    // Função para exportar relatório de custos no novo formato (atualizada para lidar com costBreakdown)
    const exportarRelatorioCustosNovoFormato = (format) => {
        const dataWithTaxa = calcularCustosComTaxa();

        // Para admins, incluímos os detalhes do costBreakdown
        const dataToExport = dataWithTaxa.flatMap(cliente => {
            // Linha principal do cliente
            const clienteRow = {
                'Tipo': 'Cliente',
                'Nome': cliente.nomeCliente || 'Cliente não identificado',
                'Email': cliente.emailCliente || '',
                'Chamadas': cliente.totalChamadas || 0,
                'Custo Base': fmt4(cliente.custoBaseTotal),
                'Taxas': fmt4(cliente.taxasTotais),
                'Custo Final': fmt4(cliente.custoFinalTotal),
                'Custo com Taxa': fmt4(cliente.custoComTaxa),
                'Diferença': fmt4(num(cliente.custoComTaxa) - num(cliente.custoFinalTotal)),
                'Assistente': '',
                'Modelo Assistente': ''
            };

            // Para admins, adicionamos colunas de costBreakdown
                if (isAdmin) {
                clienteRow['STT'] = fmt4(cliente.stt);
                clienteRow['LLM'] = fmt4(cliente.llm);
                clienteRow['TTS'] = fmt4(cliente.tts);
                clienteRow['provedor'] = fmt4(cliente.vapi);
                clienteRow['Chat'] = fmt4(cliente.chat);
                clienteRow['Prompt Tokens'] = cliente.llmPromptTokens?.toLocaleString() || '0';
                clienteRow['Completion Tokens'] = cliente.llmCompletionTokens?.toLocaleString() || '0';
                clienteRow['TTS Characters'] = cliente.ttsCharacters?.toLocaleString() || '0';
            }

            // Linhas para cada assistente
            const assistentesRows = cliente.assistentes?.map(assistente => {
                const row = {
                    'Tipo': 'Assistente',
                    'Nome': assistente.nomeAssistente || 'Assistente não identificado',
                    'Email': '',
                    'Chamadas': assistente.totalChamadas || 0,
                    'Custo Base': fmt4(assistente.custoBaseTotal),
                    'Taxas': fmt4(assistente.taxasTotais),
                    'Custo Final': fmt4(assistente.custoFinalTotal),
                    'Custo com Taxa': '',
                    'Diferença': '',
                    'Assistente': assistente.nomeAssistente || '',
                    'Modelo Assistente': assistente.modeloAssistente || ''
                };

                // Para admins, adicionamos os detalhes do costBreakdown para assistentes
                if (isAdmin && assistente.costBreakdown) {
                    row['STT'] = fmt4(assistente.costBreakdown.stt);
                    row['LLM'] = fmt4(assistente.costBreakdown.llm);
                    row['TTS'] = fmt4(assistente.costBreakdown.tts);
                    row['provedor'] = fmt4(assistente.costBreakdown.vapi);
                    row['Chat'] = fmt4(assistente.costBreakdown.chat);
                    row['Prompt Tokens'] = assistente.costBreakdown.llmPromptTokens?.toLocaleString() || '0';
                    row['Completion Tokens'] = assistente.costBreakdown.llmCompletionTokens?.toLocaleString() || '0';
                    row['TTS Characters'] = assistente.costBreakdown.ttsCharacters?.toLocaleString() || '0';
                }

                return row;
            }) || [];

            return [clienteRow, ...assistentesRows];
        });

        if (dataToExport.length === 0) {
            toast.error("Nenhum dado de custo para exportar.");
            return;
        }

        const toastId = toast.loading(`Gerando relatório de custos em ${format.toUpperCase()}...`);

        if (format === 'xlsx') {
            const ws = XLSX.utils.json_to_sheet(dataToExport);
            const wb = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(wb, ws, 'Relatorio Custos');
            XLSX.writeFile(wb, 'relatorio_custos.xlsx');
            toast.success('Relatório de custos Excel gerado!', { id: toastId });
        } else if (format === 'pdf') {
            const doc = new jsPDF({ orientation: 'landscape' });
            doc.text('Relatório de Custos com Taxas', 14, 16);
            doc.setFontSize(10);
            doc.text(`Taxa por chamada: $ ${fmt4(clienteTaxa)}`, 14, 24);

            // Definir headers base
            let headers = [
                ['Tipo', 'Nome', 'Email', 'Chamadas', 'Custo Base', 'Taxas', 'Custo Final', 'Custo com Taxa', 'Diferença', 'Assistente', 'Modelo Assistente']
            ];

            // Adicionar colunas de costBreakdown se for admin
            if (isAdmin) {
                headers[0].push('STT', 'LLM', 'TTS', 'provedor', 'Chat', 'Prompt Tokens', 'Completion Tokens', 'TTS Characters');
            }

            // Preparar os dados para a tabela
            const body = dataToExport.map(row => {
                const baseRow = [
                    row.Tipo,
                    row.Nome,
                    row.Email,
                    row.Chamadas,
                    row['Custo Base'],
                    row.Taxas,
                    row['Custo Final'],
                    row['Custo com Taxa'],
                    row.Diferença,
                    row.Assistente,
                    row['Modelo Assistente']
                ];

                if (isAdmin) {
                    return [
                        ...baseRow,
                        row['STT'] || '0.0000',
                        row['LLM'] || '0.0000',
                        row['TTS'] || '0.0000',
                        row['provedor'] || '0.0000',
                        row['Chat'] || '0.0000',
                        row['Prompt Tokens'] || '0',
                        row['Completion Tokens'] || '0',
                        row['TTS Characters'] || '0'
                    ];
                }

                return baseRow;
            });

            autoTable(doc, {
                startY: 30,
                head: headers,
                body: body,
                theme: 'grid',
                styles: { fontSize: 8 },
                columnStyles: {
                    4: { cellWidth: 20 }, // Custo Base
                    5: { cellWidth: 20 }, // Taxas
                    6: { cellWidth: 20 }, // Custo Final
                    7: { cellWidth: 20 }, // Custo com Taxa
                }
            });

            doc.save('relatorio_custos.pdf');
            toast.success('Relatório de custos PDF gerado!', { id: toastId });
        }
    };

    const exportarRelatorioCustosFormatoAntigo = (format) => {
        const dataWithTaxa = calcularCustosComTaxa();
        const dataToExport = dataWithTaxa.map(item => {
            const baseRow = {
                'Cliente': item.nomeCliente || 'Cliente não identificado',
                'Total de Chamadas': item.totalChamadas,
                'Custo Base ($)': fmt4(item.custoBaseTotal),
                'Custo Final ($)': fmt4(item.custoFinalTotal),
                'Custo com Sua Taxa ($)': fmt4(item.custoComTaxa),
                'Taxa por Chamada ($)': fmt4(clienteTaxa),
                'Taxa Total ($)': fmt4(num(clienteTaxa) * num(item.totalChamadas))
            };

            // Adiciona colunas de costBreakdown se for admin e existir
            if (isAdmin && item.costBreakdown) {
                return {
                    ...baseRow,
                    'STT': fmt4(item.costBreakdown.stt),
                    'LLM': fmt4(item.costBreakdown.llm),
                    'TTS': fmt4(item.costBreakdown.tts),
                    'provedor': fmt4(item.costBreakdown.vapi),
                    'Chat': fmt4(item.costBreakdown.chat)
                };
            }

            return baseRow;
        });

        if (dataToExport.length === 0) {
            toast.error("Nenhum dado de custo para exportar.");
            return;
        }

        const toastId = toast.loading(`Gerando relatório de custos em ${format.toUpperCase()}...`);

        if (format === 'xlsx') {
            const ws = XLSX.utils.json_to_sheet(dataToExport);
            const wb = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(wb, ws, 'Relatorio Custos');
            XLSX.writeFile(wb, 'relatorio_custos.xlsx');
            toast.success('Relatório de custos Excel gerado!', { id: toastId });
        } else if (format === 'pdf') {
            const doc = new jsPDF({ orientation: 'landscape' });
            doc.text('Relatório de Custos com Taxas', 14, 16);
            doc.setFontSize(10);
            doc.text(`Taxa por chamada: $ ${fmt4(clienteTaxa)}`, 14, 24);

            // Definir headers base
            let headers = [
                ['Cliente', 'Total de Chamadas', 'Custo Base ($)', 'Custo Final ($)', 'Custo com Sua Taxa ($)', 'Taxa por Chamada ($)', 'Taxa Total ($)']
            ];

            // Adicionar colunas de costBreakdown se for admin
            if (isAdmin && dataToExport[0]?.STT) {
                headers[0].push('STT', 'LLM', 'TTS', 'provedor', 'Chat');
            }

            const body = dataToExport.map(row => {
                const baseRow = [
                    row['Cliente'],
                    row['Total de Chamadas'],
                    row['Custo Base ($)'],
                    row['Custo Final ($)'],
                    row['Custo com Sua Taxa ($)'],
                    row['Taxa por Chamada ($)'],
                    row['Taxa Total ($)']
                ];

                if (isAdmin && row.STT) {
                    return [
                        ...baseRow,
                        row['STT'],
                        row['LLM'],
                        row['TTS'],
                        row['provedor'],
                        row['Chat']
                    ];
                }

                return baseRow;
            });

            autoTable(doc, {
                startY: 30,
                head: headers,
                body: body,
                theme: 'grid',
                styles: { fontSize: 8 },
                columnStyles: {
                    2: { cellWidth: 25 }, // Custo Base
                    3: { cellWidth: 25 }, // Custo Final
                    4: { cellWidth: 30 }, // Custo com Sua Taxa
                }
            });

            doc.save('relatorio_custos.pdf');
            toast.success('Relatório de custos PDF gerado!', { id: toastId });
        }
    };

    const exportarRelatorioCustos = (format) => {
        if (isNewFormat) {
            exportarRelatorioCustosNovoFormato(format);
        } else {
            exportarRelatorioCustosFormatoAntigo(format);
        }
    };

    const exportar = (format) => {
        if (campanhasFiltradas.length === 0) {
            toast.error("Nenhum dado para exportar com os filtros atuais.");
            return;
        }
        const dataToExport = campanhasFiltradas.map(c => ({
            'ID': c._id,
            'Nome': c.nome,
            'Cliente': c.clienteId?.nome || 'N/A',
            'Status': c.status,
            'Contatos': c.listaDeContatos?.length || 0,
            'Sucesso': Object.values(c.resultado || {}).filter(r => r.status === 'atendido').length,
            'Criado Em': new Date(c.criadoEm).toLocaleString()
        }));
        const toastId = toast.loading(`Gerando relatório em ${format.toUpperCase()}...`);
        if (format === 'xlsx') {
            const ws = XLSX.utils.json_to_sheet(dataToExport);
            const wb = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(wb, ws, 'Relatorio Campanhas');
            XLSX.writeFile(wb, 'relatorio_campanhas.xlsx');
            toast.success('Relatório Excel gerado!', { id: toastId });
        } else if (format === 'pdf') {
            const doc = new jsPDF({ orientation: 'landscape' });
            doc.text('Relatório de Campanhas', 14, 16);
            autoTable(doc, {
                startY: 20,
                head: [Object.keys(dataToExport[0])],
                body: dataToExport.map(Object.values),
                theme: 'grid',
                styles: { fontSize: 8 },
            });
            doc.save('relatorio_campanhas.pdf');
            toast.success('Relatório PDF gerado!', { id: toastId });
        }
    };

    const inputStyle = "w-full px-3 py-2 text-sm text-gray-200 bg-gray-700 border border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500";
    const labelStyle = "block text-sm font-medium text-gray-300 mb-1";

    // Dados para exibição da taxa do cliente
    const taxaInfo = [
        { label: 'Taxa por Chamada', value: `$ ${fmt4(clienteTaxa)}` },
        { label: 'Aplicação', value: 'Adicionada por chamada' },
    ];

    return (
        <div className="space-y-6">
            <h1 className="text-3xl font-bold mb-6">Relatórios de Campanhas</h1>
            {/* Card de informações do cliente */}
            {clienteId && (
                <div className="bg-gray-800 bg-opacity-60 p-6 rounded-xl shadow-lg mb-6">
                    <h3 className="text-xl font-bold mb-4">Informações do Cliente</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="bg-gray-700 p-4 rounded-lg">
                            <p className="text-gray-400 text-sm">ID do Cliente</p>
                            <p className="text-lg font-bold">{clienteId}</p>
                        </div>
                        <div className="bg-gray-700 p-4 rounded-lg">
                            <p className="text-gray-400 text-sm">Taxa por Chamada</p>
                            <div className="flex items-center gap-2">
                                <p className="text-lg font-bold">$ {fmt4(clienteTaxa)}</p>
                            </div>
                        </div>
                    </div>
                </div>
            )}
            {/* Painel de Filtros para Campanhas */}
            <div className="bg-gray-800 bg-opacity-60 p-6 rounded-xl shadow-lg mb-6">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 xl:grid-cols-6 gap-4 items-end">
                    {/* Filtros */}
                    <div>
                        <label className={labelStyle}>Data Inicial</label>
                        <input
                            type="date"
                            name="dataInicial"
                            value={filtros.dataInicial}
                            onChange={handleFilterChange}
                            className={inputStyle}
                        />
                    </div>
                    <div>
                        <label className={labelStyle}>Data Final</label>
                        <input
                            type="date"
                            name="dataFinal"
                            value={filtros.dataFinal}
                            onChange={handleFilterChange}
                            className={inputStyle}
                        />
                    </div>
                    <div>
                        <label className={labelStyle}>Tipo</label>
                        <select name="tipo" value={filtros.tipo} onChange={handleFilterChange} className={inputStyle}>
                            <option value="">Todos</option>
                            <option value="vendas">Vendas</option>
                            <option value="suporte">Suporte</option>
                        </select>
                    </div>
                    <div>
                        <label className={labelStyle}>Status</label>
                        <select name="status" value={filtros.status} onChange={handleFilterChange} className={inputStyle}>
                            <option value="">Todos</option>
                            <option value="em_fila">Em Fila</option>
                            <option value="ativa">Ativa</option>
                            <option value="falhou">Falhou</option>
                            <option value="cancelada">Cancelada</option>
                        </select>
                    </div>
                    {/* Botões de Ação */}
                    <div className="flex items-center gap-2 xl:col-span-2 justify-end">
                        <button
                            onClick={() => setFiltros(prev => ({ ...prev, dataInicial: '', dataFinal: '', tipo: '', status: '' }))}
                            className="flex items-center w-full md:w-auto justify-center px-4 py-2 bg-gray-600 rounded-lg font-semibold hover:bg-gray-700 transition-colors"
                        >
                            Limpar Filtros
                        </button>
                        <button
                            onClick={fetchCampaigns}
                            className="flex items-center w-full md:w-auto justify-center px-4 py-2 bg-gray-600 rounded-lg font-semibold hover:bg-gray-700 transition-colors"
                        >
                            <ArrowPathIcon className="h-5 w-5 mr-2" />
                            Atualizar
                        </button>
                    </div>
                    <div className="flex items-center gap-2 xl:col-span-6 justify-end">
                        <button
                            onClick={() => exportar('xlsx')}
                            className="flex items-center w-full md:w-auto justify-center px-4 py-2 bg-gray-600 rounded-lg font-semibold hover:bg-gray-700 transition-colors"
                        >
                            <ArrowDownTrayIcon className="h-5 w-5 mr-2" /> Excel
                        </button>
                        <button
                            onClick={() => exportar('pdf')}
                            className="flex items-center w-full md:w-auto justify-center px-4 py-2 bg-gray-600 rounded-lg font-semibold hover:bg-gray-700 transition-colors"
                        >
                            <ArrowDownTrayIcon className="h-5 w-5 mr-2" /> PDF
                        </button>
                    </div>
                </div>
            </div>

            {isSuperAdmin && (
                <div className="mb-12">
                    <ReportSectionHeader
                        title="Histórico de Créditos Inseridos"
                        icon={<BanknotesIcon className="h-8 w-8 text-emerald-400" />}
                    />
                    <div className="bg-gray-800 bg-opacity-60 p-4 rounded-xl shadow-lg mb-4">
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 items-end">
                            <div>
                                <label className={labelStyle}>Filtrar</label>
                                <input
                                    className={inputStyle}
                                    placeholder="Cliente, e-mail, admin ou descrição"
                                    value={creditHistoryFilter}
                                    onChange={(e) => setCreditHistoryFilter(e.target.value)}
                                />
                            </div>
                            <div className="md:col-span-2 flex justify-end gap-2">
                                <button
                                    onClick={() => {
                                        setCreditHistoryFilter('');
                                        fetchCreditHistory(1, '');
                                    }}
                                    className="px-4 py-2 bg-gray-600 rounded-lg font-semibold hover:bg-gray-700 transition-colors"
                                >
                                    Limpar
                                </button>
                                <button
                                    onClick={() => fetchCreditHistory(1, creditHistoryFilter)}
                                    className="px-4 py-2 bg-emerald-600 rounded-lg font-semibold hover:bg-emerald-700 transition-colors"
                                >
                                    Atualizar
                                </button>
                            </div>
                        </div>
                    </div>
                    <div className="bg-gray-800 bg-opacity-60 rounded-xl shadow-lg overflow-x-auto">
                        <table className="w-full text-left">
                            <thead className="bg-gray-700 bg-opacity-50">
                                <tr>
                                    <th className="p-4 font-semibold">Data</th>
                                    <th className="p-4 font-semibold">Cliente</th>
                                    <th className="p-4 font-semibold">Inserido por</th>
                                    <th className="p-4 font-semibold text-right">Crédito Inserido</th>
                                    <th className="p-4 font-semibold text-right">Saldo Antes</th>
                                    <th className="p-4 font-semibold text-right">Saldo Depois</th>
                                    <th className="p-4 font-semibold">Descrição</th>
                                </tr>
                            </thead>
                            <tbody>
                                {creditHistoryLoading ? (
                                    <tr><td colSpan={7} className="text-center p-8">Carregando histórico de créditos...</td></tr>
                                ) : creditHistoryError ? (
                                    <tr><td colSpan={7} className="text-center p-8 text-red-400">{creditHistoryError}</td></tr>
                                ) : creditHistoryItems.length === 0 ? (
                                    <tr><td colSpan={7} className="text-center p-8">Nenhuma inserção de crédito encontrada.</td></tr>
                                ) : (
                                    creditHistoryItems.map((item) => (
                                        <tr key={item._id} className="border-b border-gray-700 hover:bg-gray-700 transition-colors">
                                            <td className="p-4 text-sm">{item.createdAt ? format(new Date(item.createdAt), 'dd/MM/yyyy HH:mm') : '-'}</td>
                                            <td className="p-4">
                                                <div className="font-medium">{item.cliente?.nome || 'Sem nome'}</div>
                                                <div className="text-xs text-gray-400">{item.cliente?.email || '-'}</div>
                                            </td>
                                            <td className="p-4">
                                                <div className="font-medium">{item.inseridoPor?.nome || 'Sistema'}</div>
                                                <div className="text-xs text-gray-400">{item.inseridoPor?.email || '-'}</div>
                                            </td>
                                            <td className="p-4 text-right font-mono text-emerald-300">{fmt4(item.quantidade)}</td>
                                            <td className="p-4 text-right font-mono">{fmt4(item.saldoAnterior)}</td>
                                            <td className="p-4 text-right font-mono">{fmt4(item.saldoPosterior)}</td>
                                            <td className="p-4 text-sm text-gray-300">{item.descricao || '-'}</td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                        {creditHistoryItems.length > 0 && (
                            <div className="flex justify-between items-center p-4">
                                <button
                                    onClick={() => fetchCreditHistory(Math.max(1, creditHistoryPage - 1), creditHistoryFilter)}
                                    disabled={creditHistoryPage <= 1 || creditHistoryLoading}
                                    className="px-4 py-2 text-sm font-semibold rounded-lg bg-gray-700 hover:bg-emerald-600 disabled:bg-gray-900 disabled:text-gray-500 disabled:cursor-not-allowed transition-colors"
                                >
                                    Anterior
                                </button>
                                <span className="text-sm text-gray-400">Página {creditHistoryPage} de {creditHistoryTotalPages}</span>
                                <button
                                    onClick={() => fetchCreditHistory(Math.min(creditHistoryTotalPages, creditHistoryPage + 1), creditHistoryFilter)}
                                    disabled={creditHistoryPage >= creditHistoryTotalPages || creditHistoryLoading}
                                    className="px-4 py-2 text-sm font-semibold rounded-lg bg-gray-700 hover:bg-emerald-600 disabled:bg-gray-900 disabled:text-gray-500 disabled:cursor-not-allowed transition-colors"
                                >
                                    Próxima
                                </button>
                            </div>
                        )}
                    </div>
                </div>
            )}
            {/* Seção de Campanhas */}
            <div className="mb-4 mt-2">
                <ReportSectionHeader
                    title="Campanhas"
                    icon={<DocumentChartBarIcon className="h-8 w-8 text-indigo-400" />}
                />
                <p className="text-sm text-gray-400 -mt-2 mb-4">
                    Resultados das campanhas conforme os filtros aplicados acima.
                </p>
            </div>
            {/* Tabela de Resultados para Campanhas */}
            <div className="bg-gray-800 bg-opacity-60 rounded-xl shadow-lg overflow-x-auto mb-12">
                <table className="w-full text-left">
                    <thead className="bg-gray-700 bg-opacity-50">
                        <tr>
                            <th className="p-4 font-semibold">Nome</th>
                            <th className="p-4 font-semibold">Cliente</th>
                            <th className="p-4 font-semibold">Status</th>
                            <th className="p-4 font-semibold text-center">Contatos</th>
                            <th className="p-4 font-semibold text-center">Sucesso</th>
                            <th className="p-4 font-semibold">Criado em</th>
                        </tr>
                    </thead>
                    <tbody>
                        {loading ? (
                            <tr><td colSpan="6" className="text-center p-8">Carregando relatórios...</td></tr>
                        ) : error ? (
                            <tr><td colSpan="6" className="text-center p-8 text-red-400">
                                <ExclamationTriangleIcon className="h-6 w-6 inline mr-2" />{error}
                            </td></tr>
                        ) : campanhasFiltradas.length === 0 ? (
                            <tr><td colSpan="6" className="text-center p-8">Nenhuma campanha encontrada com os filtros aplicados.</td></tr>
                        ) : (
                            campanhasPaginadas.map(c => (
                                <tr key={c._id} className="border-b border-gray-700 hover:bg-gray-700 transition-colors">
                                    <td className="p-4 font-medium">{c.nome}</td>
                                    <td className="p-4">{c.clienteId?.nome || 'N/A'}</td>
                                    <td className="p-4"><StatusBadge status={c.status} /></td>
                                    <td className="p-4 text-center">{c.contatos?.length || 0}</td>
                                    <td className="p-4 text-center">{Object.values(c.resultado || {}).filter(r => r.status === 'atendido').length}</td>
                                    <td className="p-4">{new Date(c.criadoEm).toLocaleString()}</td>
                                </tr>
                            ))
                        )}
                    </tbody>
                </table>
                {campanhasFiltradas.length > 0 && (
                    <div className="flex flex-col md:flex-row justify-between items-center gap-3 p-4 bg-gray-800 rounded-b-xl">
                        <span className="text-sm text-gray-400">
                            Mostrando {campanhasPaginadas.length} de {campanhasFiltradas.length} campanhas
                        </span>
                        <div className="flex gap-2">
                            <button
                                onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                                disabled={currentPage === 1}
                                className="px-4 py-2 text-sm font-semibold rounded-lg bg-gray-700 hover:bg-purple-600 disabled:bg-gray-900 disabled:text-gray-500 disabled:cursor-not-allowed transition-colors"
                            >
                                Anterior
                            </button>
                            <span className="text-sm text-gray-400 self-center">
                                Página {currentPage} de {totalPages}
                            </span>
                            <button
                                onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                                disabled={currentPage === totalPages}
                                className="px-4 py-2 text-sm font-semibold rounded-lg bg-gray-700 hover:bg-purple-600 disabled:bg-gray-900 disabled:text-gray-500 disabled:cursor-not-allowed transition-colors"
                            >
                                Próxima
                            </button>
                        </div>
                    </div>
                )}
            </div>
            {/* Seção Fila URA */}
            <div className="mb-12">
                <div className="flex flex-wrap justify-between items-center gap-3">
                    <ReportSectionHeader
                        title="Fila URA"
                        icon={<ChartBarIcon className="h-8 w-8 text-blue-400" />}
                    />
                    <div className="flex flex-wrap gap-2">
                        {(isAdmin || isSuperAdmin) && (
                            <button
                                type="button"
                                onClick={releaseUraStuckQueue}
                                disabled={uraReleaseLoading}
                                className="flex items-center px-4 py-2 bg-amber-700 rounded-lg font-semibold hover:bg-amber-600 transition-colors disabled:opacity-50"
                                title="Encerra itens presos em em progresso (ex.: webhook não chegou) e libera a fila"
                            >
                                <ExclamationTriangleIcon className="h-5 w-5 mr-2" />
                                {uraReleaseLoading ? 'Liberando…' : 'Liberar fila travada'}
                            </button>
                        )}
                        <button
                            onClick={refreshUraQueueManual}
                            className="flex items-center px-4 py-2 bg-blue-600 rounded-lg font-semibold hover:bg-blue-700 transition-colors"
                        >
                            <ArrowPathIcon className="h-5 w-5 mr-2" />
                            Atualizar Fila
                        </button>
                    </div>
                </div>

                <div className="bg-gray-800 bg-opacity-60 p-6 rounded-xl shadow-lg mb-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4 items-end">
                        <div>
                            <label className={labelStyle}>Status</label>
                            <select
                                className={inputStyle}
                                value={uraQueueFilters.status}
                                onChange={(e) => setUraQueueFilters(prev => ({ ...prev, status: e.target.value }))}
                            >
                                <option value="">Todos</option>
                                <option value="pending">Em fila</option>
                                <option value="in_progress">Em progresso</option>
                                <option value="done">Atendido</option>
                                <option value="failed">Falhou</option>
                            </select>
                        </div>
                        {(isAdmin || isSuperAdmin) && (
                            <div>
                                <label className={labelStyle}>Cliente</label>
                                <select
                                    className={inputStyle}
                                    value={uraQueueFilters.clienteId}
                                    onChange={(e) => setUraQueueFilters(prev => ({ ...prev, clienteId: e.target.value }))}
                                >
                                    <option value="">Todos</option>
                                    {uraClientOptions.map((opt) => (
                                        <option key={opt.id} value={opt.id}>{opt.label}</option>
                                    ))}
                                </select>
                            </div>
                        )}
                        <div>
                            <label className={labelStyle}>PhoneNumberId (SIP)</label>
                            <input
                                className={inputStyle}
                                placeholder="Filtrar por PhoneNumberId"
                                value={uraQueueFilters.phoneNumberId}
                                onChange={(e) => setUraQueueFilters(prev => ({ ...prev, phoneNumberId: e.target.value }))}
                            />
                        </div>
                        <div className="flex gap-2">
                            <button
                                onClick={() => setUraQueueFilters({ status: '', clienteId: '', phoneNumberId: '' })}
                                className="px-4 py-2 bg-gray-600 rounded-lg font-semibold hover:bg-gray-700 transition-colors"
                            >
                                Limpar
                            </button>
                            <button
                                onClick={refreshUraQueueManual}
                                className="px-4 py-2 bg-blue-600 rounded-lg font-semibold hover:bg-blue-700 transition-colors"
                            >
                                Aplicar
                            </button>
                        </div>
                    </div>
                </div>

                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
                    <div className="bg-amber-950/30 border border-amber-800 rounded-lg p-3">
                        <div className="text-xs text-gray-400">Em fila</div>
                        <div className="text-xl font-bold">{uraQueueSummary.pending || 0}</div>
                    </div>
                    <div className="bg-blue-950/30 border border-blue-800 rounded-lg p-3">
                        <div className="text-xs text-gray-400">Em progresso</div>
                        <div className="text-xl font-bold">{uraQueueSummary.in_progress || 0}</div>
                    </div>
                    <div className="bg-green-950/30 border border-green-800 rounded-lg p-3">
                        <div className="text-xs text-gray-400">Atendidas</div>
                        <div className="text-xl font-bold">{uraQueueSummary.done || 0}</div>
                    </div>
                    <div className="bg-rose-950/30 border border-rose-800 rounded-lg p-3">
                        <div className="text-xs text-gray-400">Falhas</div>
                        <div className="text-xl font-bold">{uraQueueSummary.failed || 0}</div>
                    </div>
                </div>

                <div className="bg-gray-800 bg-opacity-60 rounded-xl shadow-lg overflow-x-auto">
                    {uraQueueLoading ? (
                        <div className="p-6 text-center">Carregando fila URA...</div>
                    ) : uraQueueError ? (
                        <div className="p-6 text-center text-red-400">{uraQueueError}</div>
                    ) : uraQueueGroups.length === 0 ? (
                        <div className="p-6 text-center">Nenhum item na fila URA.</div>
                    ) : (
                        <table className="w-full text-left">
                            <thead className="bg-gray-700 bg-opacity-50">
                                <tr>
                                    <th className="p-4"></th>
                                    <th className="p-4 font-semibold">Cliente</th>
                                    <th className="p-4 font-semibold text-center">Total</th>
                                    <th className="p-4 font-semibold text-center">Em fila</th>
                                    <th className="p-4 font-semibold text-center">Em progresso</th>
                                    <th className="p-4 font-semibold text-center">Atendidas</th>
                                    <th className="p-4 font-semibold text-center">Falhas</th>
                                </tr>
                            </thead>
                            <tbody>
                                {uraQueueGroups.map((group) => (
                                    <React.Fragment key={group.clienteId || group.nomeCliente}>
                                        <tr className="border-b border-gray-700 hover:bg-gray-700 transition-colors">
                                            <td className="p-4">
                                                <button
                                                    onClick={() => toggleQueueClientExpanded(group.clienteId || group.nomeCliente)}
                                                    className="p-1 rounded-full hover:bg-gray-600 transition-colors"
                                                >
                                                    {expandedQueueClients[group.clienteId || group.nomeCliente]
                                                        ? <ChevronUpIcon className="h-5 w-5" />
                                                        : <ChevronDownIcon className="h-5 w-5" />}
                                                </button>
                                            </td>
                                            <td className="p-4 font-medium">
                                                <div>{group.nomeCliente}</div>
                                                <div className="text-xs text-gray-400">{group.emailCliente || 'Sem e-mail'}</div>
                                            </td>
                                            <td className="p-4 text-center">{group.total}</td>
                                            <td className="p-4 text-center">{group.summary?.pending || 0}</td>
                                            <td className="p-4 text-center">{group.summary?.in_progress || 0}</td>
                                            <td className="p-4 text-center">{group.summary?.done || 0}</td>
                                            <td className="p-4 text-center">{group.summary?.failed || 0}</td>
                                        </tr>

                                        {expandedQueueClients[group.clienteId || group.nomeCliente] && group.phoneNumbers?.map((pnGroup) => (
                                            <React.Fragment key={`${group.clienteId || 'sem'}-${pnGroup.phoneNumberId || 'sem'}`}>
                                                <tr className="bg-gray-900/50 border-b border-gray-700">
                                                    <td className="p-4"></td>
                                                    <td className="p-4" colSpan={6}>
                                                        <button
                                                            type="button"
                                                            onClick={() => toggleQueuePhoneExpanded(group.clienteId, pnGroup.phoneNumberId)}
                                                            className="flex items-start gap-2 text-left w-full font-semibold text-blue-300"
                                                        >
                                                            {expandedQueuePhoneNumbers[`${group.clienteId || 'sem-cliente'}:${pnGroup.phoneNumberId || 'sem-phone'}`]
                                                                ? <ChevronUpIcon className="h-4 w-4 shrink-0 mt-0.5" />
                                                                : <ChevronDownIcon className="h-4 w-4 shrink-0 mt-0.5" />}
                                                            <span className="flex flex-col gap-0.5">
                                                                <span>
                                                                    {pnGroup.phoneNumberDisplay
                                                                        || pnGroup.phoneNumberName
                                                                        || 'Phone number'}
                                                                </span>
                                                                <span className="text-xs font-normal text-gray-400 font-mono break-all">
                                                                    ID: {pnGroup.phoneNumberId || 'N/A'}
                                                                    {pnGroup.phoneNumberLine ? ` · ${pnGroup.phoneNumberLine}` : ''}
                                                                    {' · '}
                                                                    Total: {pnGroup.total}
                                                                </span>
                                                            </span>
                                                        </button>
                                                    </td>
                                                </tr>
                                                {expandedQueuePhoneNumbers[`${group.clienteId || 'sem-cliente'}:${pnGroup.phoneNumberId || 'sem-phone'}`] && (
                                                    <tr className="bg-gray-900/30 border-b border-gray-700">
                                                        <td className="p-4"></td>
                                                        <td className="p-4" colSpan={6}>
                                                            {(() => {
                                                                const groupKey = `${group.clienteId || 'sem-cliente'}:${pnGroup.phoneNumberId || 'sem-phone'}`;
                                                                const pageData = queueGroupItemsMap[groupKey] || { items: [], page: 1, totalPages: 1, total: 0 };
                                                                const groupLoading = queueGroupLoadingMap[groupKey];
                                                                return (
                                                            <div className="overflow-x-auto">
                                                                <table className="w-full text-sm">
                                                                    <thead>
                                                                        <tr className="text-gray-300">
                                                                            <th className="p-2 text-left">Telefone</th>
                                                                            <th className="p-2 text-left">Status</th>
                                                                            <th className="p-2 text-left">Tentativas</th>
                                                                            <th className="p-2 text-left">Criada em</th>
                                                                            <th className="p-2 text-left">Iniciada em</th>
                                                                            <th className="p-2 text-left">Finalizada em</th>
                                                                        </tr>
                                                                    </thead>
                                                                    <tbody>
                                                                        {groupLoading ? (
                                                                            <tr>
                                                                                <td className="p-2" colSpan={6}>Carregando chamadas...</td>
                                                                            </tr>
                                                                        ) : pageData.items?.length === 0 ? (
                                                                            <tr>
                                                                                <td className="p-2" colSpan={6}>Nenhuma chamada encontrada para este grupo.</td>
                                                                            </tr>
                                                                        ) : pageData.items?.map((item) => (
                                                                            <tr key={item._id} className="border-t border-gray-700">
                                                                                <td className="p-2 font-mono">{item.telefoneE164 || item.telefoneOriginal}</td>
                                                                                <td className="p-2"><StatusBadge status={item.statusLabel || item.status} /></td>
                                                                                <td className="p-2">{item.attempts || 0}</td>
                                                                                <td className="p-2">{item.createdAt ? new Date(item.createdAt).toLocaleString() : '-'}</td>
                                                                                <td className="p-2">{item.startedAt ? new Date(item.startedAt).toLocaleString() : '-'}</td>
                                                                                <td className="p-2">{item.finishedAt ? new Date(item.finishedAt).toLocaleString() : '-'}</td>
                                                                            </tr>
                                                                        ))}
                                                                    </tbody>
                                                                </table>
                                                                <div className="flex items-center justify-between mt-3 text-xs text-gray-300">
                                                                    <span>
                                                                        Mostrando {pageData.items?.length || 0} de {pageData.total || 0} chamadas
                                                                    </span>
                                                                    <div className="flex items-center gap-2">
                                                                        <button
                                                                            disabled={(pageData.page || 1) <= 1 || groupLoading}
                                                                            onClick={() => fetchQueueGroupItems(group.clienteId, pnGroup.phoneNumberId, (pageData.page || 1) - 1)}
                                                                            className="px-2 py-1 rounded bg-gray-700 disabled:opacity-50"
                                                                        >
                                                                            Anterior
                                                                        </button>
                                                                        <span>Página {pageData.page || 1} de {pageData.totalPages || 1}</span>
                                                                        <button
                                                                            disabled={(pageData.page || 1) >= (pageData.totalPages || 1) || groupLoading}
                                                                            onClick={() => fetchQueueGroupItems(group.clienteId, pnGroup.phoneNumberId, (pageData.page || 1) + 1)}
                                                                            className="px-2 py-1 rounded bg-gray-700 disabled:opacity-50"
                                                                        >
                                                                            Próxima
                                                                        </button>
                                                                    </div>
                                                                </div>
                                                            </div>
                                                                );
                                                            })()}
                                                        </td>
                                                    </tr>
                                                )}
                                            </React.Fragment>
                                        ))}
                                    </React.Fragment>
                                ))}
                            </tbody>
                        </table>
                    )}
                </div>
            </div>
            {/* Seção de Relatórios de Custo */}
            <div className="mb-12">
                <div className="flex justify-between items-center">
                    <div className="flex items-center gap-2">
                        <ReportSectionHeader
                            title="Relatório de Custo por Cliente"
                            icon={<BanknotesIcon className="h-8 w-8 text-purple-400" />}
                        />
                        {isAdmin && (
                            <span className="px-2 py-1 text-xs font-semibold rounded-full bg-purple-900 text-purple-200 border border-purple-500">
                                <InformationCircleIcon className="h-4 w-4 inline mr-1" />
                                Modo Administrador - Detalhes completos
                            </span>
                        )}
                    </div>
                    <div className="flex gap-2">
                        <button
                            onClick={fetchCostReport}
                            className="flex items-center px-4 py-2 bg-purple-600 rounded-lg font-semibold hover:bg-purple-700 transition-colors"
                        >
                            <ArrowPathIcon className="h-5 w-5 mr-2" />
                            Atualizar
                        </button>
                        <button
                            onClick={() => exportarRelatorioCustos('xlsx')}
                            className="flex items-center px-4 py-2 bg-purple-600 rounded-lg font-semibold hover:bg-purple-700 transition-colors"
                        >
                            <ArrowDownTrayIcon className="h-5 w-5 mr-2" /> Excel
                        </button>
                        <button
                            onClick={() => exportarRelatorioCustos('pdf')}
                            className="flex items-center px-4 py-2 bg-purple-600 rounded-lg font-semibold hover:bg-purple-700 transition-colors"
                        >
                            <ArrowDownTrayIcon className="h-5 w-5 mr-2" /> PDF
                        </button>
                    </div>
                </div>
                {/* Legenda dos custos apenas para admin */}
                {isAdmin && (
                    <div className="bg-gray-800 bg-opacity-60 p-4 rounded-lg mb-4">
                        <div className="flex items-center gap-2 mb-2">
                            <InformationCircleIcon className="h-5 w-5 text-purple-400" />
                            <h3 className="font-semibold">Legenda dos Custos Detalhados</h3>
                        </div>
                        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-2 text-sm">
                            <div className="bg-gray-700 p-2 rounded">
                                <div className="font-bold text-blue-300">STT</div>
                                <div className="text-gray-400 text-xs">Speech-to-Text</div>
                            </div>
                            <div className="bg-gray-700 p-2 rounded">
                                <div className="font-bold text-green-300">LLM</div>
                                <div className="text-gray-400 text-xs">Modelo de Linguagem</div>
                            </div>
                            <div className="bg-gray-700 p-2 rounded">
                                <div className="font-bold text-purple-300">TTS</div>
                                <div className="text-gray-400 text-xs">Text-to-Speech</div>
                            </div>
                            <div className="bg-gray-700 p-2 rounded">
                                <div className="font-bold text-yellow-300">provedor</div>
                                <div className="text-gray-400 text-xs">Infraestrutura provedor</div>
                            </div>
                            <div className="bg-gray-700 p-2 rounded">
                                <div className="font-bold text-emerald-300">Chat</div>
                                <div className="text-gray-400 text-xs">Mensagens de Chat</div>
                            </div>
                        </div>
                    </div>
                )}
                {/* Painel de Filtros para Relatórios de Custo */}
                <div className="bg-gray-800 bg-opacity-60 p-6 rounded-xl shadow-lg mb-6">
                    <div className="flex justify-between items-center mb-4">
                        <h3 className="text-xl font-bold">Filtros de Custo</h3>
                        <div className="flex gap-2">
                            <button
                                onClick={() => setCustoFiltros({ dataInicial: '', dataFinal: '' })}
                                className="flex items-center px-4 py-2 bg-gray-600 rounded-lg font-semibold hover:bg-gray-700 transition-colors"
                            >
                                Limpar Filtros
                            </button>
                            <button
                                onClick={fetchCostReport}
                                className="flex items-center px-4 py-2 bg-purple-600 rounded-lg font-semibold hover:bg-purple-700 transition-colors"
                            >
                                <FunnelIcon className="h-5 w-5 mr-2" />
                                Aplicar Filtros
                            </button>
                        </div>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 items-end">
                        {/* Campos de data para filtragem */}
                        <div>
                            <label className={labelStyle}>Data Inicial</label>
                            <input
                                type="date"
                                name="dataInicial"
                                value={custoFiltros.dataInicial}
                                onChange={(e) => setCustoFiltros(prev => ({ ...prev, dataInicial: e.target.value }))}
                                className={inputStyle}
                            />
                        </div>
                        <div>
                            <label className={labelStyle}>Data Final</label>
                            <input
                                type="date"
                                name="dataFinal"
                                value={custoFiltros.dataFinal}
                                onChange={(e) => setCustoFiltros(prev => ({ ...prev, dataFinal: e.target.value }))}
                                className={inputStyle}
                            />
                        </div>
                        <div>
                            <label className={labelStyle}>Cliente</label>
                            <input
                                type="text"
                                placeholder="Filtrar por nome, e-mail ou ID"
                                value={costClienteFiltro}
                                onChange={(e) => setCostClienteFiltro(e.target.value)}
                                className={inputStyle}
                            />
                        </div>
                    </div>
                </div>
                {/* Card explicativo sobre a taxa */}
                <div className="bg-gray-800 bg-opacity-60 p-4 rounded-lg mb-4">
                    <div className="flex items-center gap-2 mb-2">
                        <ChartBarIcon className="h-5 w-5 text-purple-400" />
                        <h3 className="font-semibold">Cálculo de Custos com Sua Taxa</h3>
                    </div>
                    <p className="text-sm text-gray-300 mb-2">
                        Os valores abaixo incluem sua taxa fixa de $ {fmt4(clienteTaxa)} por chamada.
                        A fórmula usada é: <code className="bg-gray-700 px-1 rounded">Custo Base + (Taxa por Chamada × Número de Chamadas)</code>
                    </p>
                    <div className="text-xs grid grid-cols-2 md:grid-cols-4 gap-2">
                        {taxaInfo.map((info, index) => (
                            <div key={index} className="bg-gray-700 p-2 rounded">
                                <div className="text-gray-400">{info.label}</div>
                                <div className="font-bold">{info.value}</div>
                            </div>
                        ))}
                    </div>
                </div>
                {/* Tabela de custos */}
                <div className="bg-gray-800 bg-opacity-60 rounded-xl shadow-lg overflow-x-auto">
                    {isNewFormat ? (
                        // Renderização para o novo formato (com assistentes)
                        <>
                            <table className="w-full text-left">
                                <thead className="bg-gray-700 bg-opacity-50">
                                    <tr>
                                        <th className="p-4 font-semibold"></th>
                                        <th className="p-4 font-semibold">Cliente</th>
                                        <th className="p-4 font-semibold text-center">Total de Chamadas</th>
                                        {isSuperAdmin && <th className="p-4 font-semibold text-center">Minutos</th>}
                                        {isAdmin && <th className="p-4 font-semibold text-right">Custo Base ($)</th>}
                                        {isAdmin && (
                                            <th className="p-4 font-semibold text-right">
                                                Taxa do Cliente ($)
                                                <div className="text-xs font-normal text-gray-300">por chamada</div>
                                            </th>
                                        )}
                                        <th className="p-4 font-semibold text-right">Custo Final ($)</th>
                                        {/* Adicionamos colunas para costBreakdown se for admin */}
                                        {isAdmin && (
                                            <>
                                                <th className="p-4 font-semibold text-right">STT</th>
                                                <th className="p-4 font-semibold text-right">LLM</th>
                                                <th className="p-4 font-semibold text-right">TTS</th>
                                                <th className="p-4 font-semibold text-right">provedor</th>
                                                <th className="p-4 font-semibold text-right">Chat</th>
                                            </>
                                        )}
                                    </tr>
                                </thead>
                                <tbody>
                                    {costLoading ? (
                                        <tr><td colSpan={isSuperAdmin ? (isAdmin ? 12 : 6) : (isAdmin ? 11 : 5)} className="text-center p-8">Carregando relatório de custos...</td></tr>
                                    ) : costError ? (
                                        <tr><td colSpan={isSuperAdmin ? (isAdmin ? 12 : 6) : (isAdmin ? 11 : 5)} className="text-center p-8 text-red-400">
                                            <ExclamationTriangleIcon className="h-6 w-6 inline mr-2" />{costError}
                                        </td></tr>
                                    ) : custosComTaxaFiltrados.length === 0 ? (
                                        <tr><td colSpan={isSuperAdmin ? (isAdmin ? 12 : 6) : (isAdmin ? 11 : 5)} className="text-center p-8">Nenhum dado de custo encontrado.</td></tr>
                                    ) : (
                                        custosComTaxaPaginados.map(cliente => (
                                            <React.Fragment key={cliente.clienteId}>
                                                {/* Linha principal do cliente */}
                                                <tr className="border-b border-gray-700 hover:bg-gray-700 transition-colors">
                                                    <td className="p-4">
                                                        <button
                                                            onClick={() => toggleClientExpanded(cliente.clienteId)}
                                                            className="p-1 rounded-full hover:bg-gray-600 transition-colors"
                                                        >
                                                            {expandedClients[cliente.clienteId] ? (
                                                                <ChevronUpIcon className="h-5 w-5" />
                                                            ) : (
                                                                <ChevronDownIcon className="h-5 w-5" />
                                                            )}
                                                        </button>
                                                    </td>
                                                    <td className="p-4 font-bold">{cliente.nomeCliente || 'Cliente não identificado'}</td>
                                                    <td className="p-4 text-center font-mono">{cliente.totalChamadas}</td>
                                                    {isSuperAdmin && <td className="p-4 text-center font-mono">{fmtMin(cliente.totalMinutos)}</td>}
                                                    {isAdmin && <td className="p-4 text-right font-mono text-blue-300">{fmt4(cliente.custoBaseTotal)}</td>}
                                                    {isAdmin && (
                                                        <td className="p-4 text-right font-mono text-purple-300">
                                                            {fmt4(cliente.taxaCliente)}
                                                        </td>
                                                    )}
                                                    <td className="p-4 text-right font-bold font-mono text-purple-300">
                                                        {fmt4(
                                                            num(cliente.custoFinalTotal)
                                                            + num(cliente.taxaCliente) * num(cliente.totalChamadas)
                                                        )}
                                                    </td>
                                                    {/* Colunas de costBreakdown para admin */}
                                                    {isAdmin && (
                                                        <>
                                                            <td className="p-4 text-right font-mono">{fmt4(cliente.stt)}</td>
                                                            <td className="p-4 text-right font-mono">{fmt4(cliente.llm)}</td>
                                                            <td className="p-4 text-right font-mono">{fmt4(cliente.tts)}</td>
                                                            <td className="p-4 text-right font-mono">{fmt4(cliente.vapi)}</td>
                                                            <td className="p-4 text-right font-mono">{fmt4(cliente.chat)}</td>
                                                        </>
                                                    )}
                                                </tr>
                                                {/* Linhas dos assistentes (se expandido) */}
                                                {expandedClients[cliente.clienteId] && cliente.assistentes?.map(assistente => {
                                                    const nomeAssistente = assistente.nomeAssistente || 'Assistente não identificado';
                                                    const modeloAssistente = typeof assistente.modeloAssistente === 'object'
                                                        ? 'Configuração complexa'
                                                        : assistente.modeloAssistente || 'Desconhecido';
                                                    return (
                                                        <tr key={`${cliente.clienteId}-${assistente.assistenteId || Math.random().toString(36)}`}
                                                            className="border-b border-gray-600 bg-gray-800/50">
                                                            <td className="p-4"></td>
                                                            <td className="p-4 pl-8 font-medium text-gray-300">
                                                                <div className="flex items-center gap-2">
                                                                    <div className="w-3 h-3 rounded-full bg-blue-500"></div>
                                                                    {nomeAssistente}
                                                                </div>
                                                                <div className="text-xs text-gray-400 mt-1">
                                                                    Modelo: {modeloAssistente}
                                                                </div>
                                                            </td>
                                                            <td className="p-4 text-center font-mono">{Number(assistente.totalChamadas || 0).toLocaleString()}</td>
                                                            {isSuperAdmin && <td className="p-4 text-center font-mono">{fmtMin(assistente.totalMinutos)}</td>}
                                                            {isAdmin && (
                                                                <td className="p-4 text-right font-mono text-blue-200">
                                                                    {Number(assistente.custoBaseTotal || 0).toFixed(4)}
                                                                </td>
                                                            )}
                                                            {isAdmin && <td className="p-4 text-right font-mono text-gray-400">-</td>}
                                                            <td className="p-4 text-right font-mono text-green-200">
                                                                {Number(assistente.custoFinalTotal || 0).toFixed(4)}
                                                            </td>
                                                            {/* Colunas de costBreakdown para assistentes (se admin) */}
                                                            {isAdmin && (
                                                                <>
                                                                    <td className="p-4 text-right font-mono">
                                                                        {fmt4(assistente.costBreakdown?.stt)}
                                                                    </td>
                                                                    <td className="p-4 text-right font-mono">
                                                                        {fmt4(assistente.costBreakdown?.llm)}
                                                                    </td>
                                                                    <td className="p-4 text-right font-mono">
                                                                        {fmt4(assistente.costBreakdown?.tts)}
                                                                    </td>
                                                                    <td className="p-4 text-right font-mono">
                                                                        {fmt4(assistente.costBreakdown?.vapi)}
                                                                    </td>
                                                                    <td className="p-4 text-right font-mono">
                                                                        {fmt4(assistente.costBreakdown?.chat)}
                                                                    </td>
                                                                </>
                                                            )}
                                                        </tr>
                                                    );
                                                })}
                                            </React.Fragment>
                                        ))
                                    )}
                                </tbody>
                                <tfoot>
                                    {custosComTaxaFiltrados.length > 0 && (
                                        <tr className="bg-gray-700 font-bold">
                                            <td className="p-4">TOTAL</td>
                                            <td className="p-4"></td>
                                            <td className="p-4 text-center">
                                                {costSummary.totalChamadas}
                                            </td>
                                            {isSuperAdmin && (
                                                <td className="p-4 text-center">
                                                    {fmtMin(costSummary.totalMinutos)}
                                                </td>
                                            )}
                                            {isAdmin && (
                                                <td className="p-4 text-right">
                                                    {fmt4(costSummary.custoBaseTotal)}
                                                </td>
                                            )}
                                            {isAdmin && <td className="p-4 text-right"></td>} {/* Taxa do Cliente - não tem total */}
                                            <td className="p-4 text-right">
                                                {fmt4(costSummary.custoFinalComTaxa)}
                                            </td>
                                            {/* Totais para costBreakdown se for admin */}
                                            {isAdmin ? ([
                                                <td key="total-stt" className="p-4 text-right">
                                                    {fmt4(costSummary.stt)}
                                                </td>,
                                                <td key="total-llm" className="p-4 text-right">
                                                    {fmt4(costSummary.llm)}
                                                </td>,
                                                <td key="total-tts" className="p-4 text-right">
                                                    {fmt4(costSummary.tts)}
                                                </td>,
                                                <td key="total-vapi" className="p-4 text-right">
                                                    {fmt4(costSummary.vapi)}
                                                </td>,
                                                <td key="total-chat" className="p-4 text-right">
                                                    {fmt4(costSummary.chat)}
                                                </td>
                                            ]) : null}
                                        </tr>
                                    )}
                                </tfoot>
                            </table>
                            {custosComTaxaFiltrados.length > 0 && (
                                <div className="flex flex-col md:flex-row justify-between items-center gap-3 p-4 bg-gray-800 rounded-b-xl">
                                    <span className="text-sm text-gray-400">
                                        Mostrando {custosComTaxaPaginados.length} de {custosComTaxaFiltrados.length} clientes
                                    </span>
                                    <div className="flex gap-2">
                                        <button
                                            onClick={() => setCostCurrentPage(prev => Math.max(prev - 1, 1))}
                                            disabled={costCurrentPage === 1}
                                            className="px-4 py-2 text-sm font-semibold rounded-lg bg-gray-700 hover:bg-purple-600 disabled:bg-gray-900 disabled:text-gray-500 disabled:cursor-not-allowed transition-colors"
                                        >
                                            Anterior
                                        </button>
                                        <span className="text-sm text-gray-400 self-center">
                                            Página {costCurrentPage} de {costTotalPages}
                                        </span>
                                        <button
                                            onClick={() => setCostCurrentPage(prev => Math.min(prev + 1, costTotalPages))}
                                            disabled={costCurrentPage === costTotalPages}
                                            className="px-4 py-2 text-sm font-semibold rounded-lg bg-gray-700 hover:bg-purple-600 disabled:bg-gray-900 disabled:text-gray-500 disabled:cursor-not-allowed transition-colors"
                                        >
                                            Próxima
                                        </button>
                                    </div>
                                </div>
                            )}
                        </>
                    ) : (
                        // Renderização para o formato antigo (sem assistentes)
                        <>
                        <table className="w-full text-left">
                            <thead className="bg-gray-700 bg-opacity-50">
                                <tr>
                                    <th className="p-4 font-semibold">Cliente</th>
                                    <th className="p-4 font-semibold text-center">Total de Chamadas</th>
                                    {isSuperAdmin && <th className="p-4 font-semibold text-center">Minutos</th>}
                                    {isAdmin && <th className="p-4 font-semibold text-right">Custo Base ($)</th>}
                                    {isAdmin && (
                                        <th className="p-4 font-semibold text-right">
                                            Taxa do Cliente ($)
                                            <div className="text-xs font-normal text-gray-300">por chamada</div>
                                        </th>
                                    )}
                                    <th className="p-4 font-semibold text-right">Custo Final ($)</th>
                                    {/* Adicionamos colunas para costBreakdown se for admin e formato antigo */}
                                    {isAdmin && (
                                        <>
                                            <th className="p-4 font-semibold text-right">STT</th>
                                            <th className="p-4 font-semibold text-right">LLM</th>
                                            <th className="p-4 font-semibold text-right">TTS</th>
                                                <th className="p-4 font-semibold text-right">provedor</th>
                                            <th className="p-4 font-semibold text-right">Chat</th>
                                        </>
                                    )}
                                </tr>
                            </thead>
                            <tbody>
                                {costLoading ? (
                                    <tr><td colSpan={isSuperAdmin ? (isAdmin ? 11 : 5) : (isAdmin ? 10 : 4)} className="text-center p-8">Carregando relatório de custos...</td></tr>
                                ) : costError ? (
                                    <tr><td colSpan={isSuperAdmin ? (isAdmin ? 11 : 5) : (isAdmin ? 10 : 4)} className="text-center p-8 text-red-400">
                                        <ExclamationTriangleIcon className="h-6 w-6 inline mr-2" />{costError}
                                    </td></tr>
                                ) : custosComTaxaFiltrados.length === 0 ? (
                                    <tr><td colSpan={isSuperAdmin ? (isAdmin ? 11 : 5) : (isAdmin ? 10 : 4)} className="text-center p-8">Nenhum dado de custo encontrado.</td></tr>
                                ) : (
                                    custosComTaxaPaginados.map(item => (
                                        <tr key={item.clienteId || item.nomeCliente} className="border-b border-gray-700 hover:bg-gray-700 transition-colors">
                                            <td className="p-4 font-medium">{item.nomeCliente || 'Cliente não identificado'}</td>
                                            <td className="p-4 text-center font-mono">{item.totalChamadas}</td>
                                            {isSuperAdmin && <td className="p-4 text-center font-mono">{fmtMin(item.totalMinutos)}</td>}
                                            {isAdmin && <td className="p-4 text-right font-mono text-blue-300">{fmt4(item.custoBaseTotal)}</td>}
                                            {isAdmin && (
                                                <td className="p-4 text-right font-mono text-purple-300">
                                                    {fmt4(item.taxaCliente)}
                                                </td>
                                            )}
                                            <td className="p-4 text-right font-bold font-mono text-purple-300">
                                                {fmt4(
                                                    num(item.custoFinalTotal)
                                                    + num(item.taxaCliente) * num(item.totalChamadas)
                                                )}
                                            </td>
                                            {/* Colunas de costBreakdown para admin (formato antigo) */}
                                            {isAdmin && (
                                                <>
                                                    <td className="p-4 text-right font-mono">{fmt4(item.costBreakdown?.stt)}</td>
                                                    <td className="p-4 text-right font-mono">{fmt4(item.costBreakdown?.llm)}</td>
                                                    <td className="p-4 text-right font-mono">{fmt4(item.costBreakdown?.tts)}</td>
                                                    <td className="p-4 text-right font-mono">{fmt4(item.costBreakdown?.vapi)}</td>
                                                    <td className="p-4 text-right font-mono">{fmt4(item.costBreakdown?.chat)}</td>
                                                </>
                                            )}
                                        </tr>
                                    ))
                                )}
                            </tbody>
                            <tfoot>
                                {custosComTaxaFiltrados.length > 0 && (
                                    <tr className="bg-gray-700 font-bold">
                                        <td className="p-4">TOTAL</td>
                                        <td className="p-4 text-center">
                                            {costSummary.totalChamadas}
                                        </td>
                                        {isSuperAdmin && (
                                            <td className="p-4 text-center">
                                                {fmtMin(costSummary.totalMinutos)}
                                            </td>
                                        )}
                                        {isAdmin && (
                                            <td className="p-4 text-right">
                                                {fmt4(costSummary.custoBaseTotal)}
                                            </td>
                                        )}
                                        {isAdmin && <td className="p-4 text-right"></td>} {/* Taxa do Cliente - não tem total */}
                                        <td className="p-4 text-right">
                                            {fmt4(costSummary.custoFinalComTaxa)}
                                        </td>
                                        {/* Totais para costBreakdown se for admin (formato antigo) */}
                                        {isAdmin ? ([
                                            <td key="legacy-total-stt" className="p-4 text-right">
                                                {fmt4(costSummary.stt)}
                                            </td>,
                                            <td key="legacy-total-llm" className="p-4 text-right">
                                                {fmt4(costSummary.llm)}
                                            </td>,
                                            <td key="legacy-total-tts" className="p-4 text-right">
                                                {fmt4(costSummary.tts)}
                                            </td>,
                                            <td key="legacy-total-vapi" className="p-4 text-right">
                                                {fmt4(costSummary.vapi)}
                                            </td>,
                                            <td key="legacy-total-chat" className="p-4 text-right">
                                                {fmt4(costSummary.chat)}
                                            </td>
                                        ]) : null}
                                    </tr>
                                )}
                            </tfoot>
                        </table>
                        {custosComTaxaFiltrados.length > 0 && (
                            <div className="flex flex-col md:flex-row justify-between items-center gap-3 p-4 bg-gray-800 rounded-b-xl">
                                <span className="text-sm text-gray-400">
                                    Mostrando {custosComTaxaPaginados.length} de {custosComTaxaFiltrados.length} clientes
                                </span>
                                <div className="flex gap-2">
                                    <button
                                        onClick={() => setCostCurrentPage(prev => Math.max(prev - 1, 1))}
                                        disabled={costCurrentPage === 1}
                                        className="px-4 py-2 text-sm font-semibold rounded-lg bg-gray-700 hover:bg-purple-600 disabled:bg-gray-900 disabled:text-gray-500 disabled:cursor-not-allowed transition-colors"
                                    >
                                        Anterior
                                    </button>
                                    <span className="text-sm text-gray-400 self-center">
                                        Página {costCurrentPage} de {costTotalPages}
                                    </span>
                                    <button
                                        onClick={() => setCostCurrentPage(prev => Math.min(prev + 1, costTotalPages))}
                                        disabled={costCurrentPage === costTotalPages}
                                        className="px-4 py-2 text-sm font-semibold rounded-lg bg-gray-700 hover:bg-purple-600 disabled:bg-gray-900 disabled:text-gray-500 disabled:cursor-not-allowed transition-colors"
                                    >
                                        Próxima
                                    </button>
                                </div>
                            </div>
                        )}
                        </>
                    )}
                </div>
            </div>
        </div>
    );
}
